import { addCalendarDays } from '@/features/team/timeEntries';

/**
 * Payment terms are free text on the invoice, but one value is special:
 * 'Due on completion' (2026-10-06) means the invoice has NO due date until the
 * job linked to it is marked complete — the database clears the date on save
 * and sets it on completion. Everything that prints a due date treats the
 * missing date as "when the job is completed", never as "overdue".
 */
export const DUE_ON_COMPLETION = 'Due on completion';

export const PAYMENT_TERM_OPTIONS: readonly string[] = [
  'Due on receipt', 'Net 15', 'Net 30', 'Net 60', 'Net 90', DUE_ON_COMPLETION,
];

export function isDueOnCompletion(terms: string | null | undefined): boolean {
  return terms === DUE_ON_COMPLETION;
}

/** What to print where a due date would go. */
export function dueLabelFor(dueDate: string | null | undefined, terms: string | null | undefined, formatted: (d: string) => string): string | null {
  if (dueDate) return formatted(dueDate);
  return isDueOnCompletion(terms) ? 'When the job is completed' : null;
}

/**
 * Blank terms ('') mean the contractor chose no payment terms (#1309). Radix
 * Select cannot hold '' as an item value, so the picker uses this sentinel and
 * maps it back to '' before saving.
 */
export const NO_PAYMENT_TERMS_VALUE = '__none__';
export const NO_PAYMENT_TERMS_LABEL = 'No payment terms';

/** Stored terms -> the picker's value. NULL (legacy, never chosen) reads as Net 30. */
export function termsForSelect(terms: string | null | undefined): string {
  if (terms == null) return 'Net 30';
  const trimmed = terms.trim();
  return trimmed === '' ? NO_PAYMENT_TERMS_VALUE : trimmed;
}

/** The picker's value -> what is saved ('' for no payment terms). */
export function termsFromSelect(value: string): string {
  return value === NO_PAYMENT_TERMS_VALUE ? '' : value;
}

/** Options for a terms picker, keeping a stored value outside the list so it round-trips. */
export function paymentTermSelectOptions(current: string | null | undefined): { value: string; label: string }[] {
  const options = [
    { value: NO_PAYMENT_TERMS_VALUE, label: NO_PAYMENT_TERMS_LABEL },
    ...PAYMENT_TERM_OPTIONS.map((term) => ({ value: term, label: term })),
  ];
  const value = termsForSelect(current);
  if (!options.some((option) => option.value === value)) options.push({ value, label: value });
  return options;
}

/**
 * The terms an invoice takes from its estimate (mirror of the database's
 * _estimate_invoice_terms): NULL keeps meaning Net 30, anything else, blank
 * included, is copied trimmed.
 */
export function invoiceTermsFromEstimate(terms: string | null | undefined): string {
  return terms == null ? 'Net 30' : terms.trim();
}

/**
 * The due date the terms give an invoice issued on `today` (YYYY-MM-DD), the
 * mirror of the database's _invoice_due_date_for_terms: Due on receipt = today,
 * Net N = today + N, anything else (blank included) = today + 30.
 */
export function dueDateForTerms(terms: string | null | undefined, today: string): string {
  const trimmed = (terms ?? '').trim();
  let days = 30;
  if (trimmed.toLowerCase() === 'due on receipt') days = 0;
  else {
    const net = /^net\s+([0-9]{1,3})$/i.exec(trimmed);
    if (net) days = Number(net[1]);
  }
  return addCalendarDays(today, days);
}
